import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ThumbsUp, ThumbsDown, Globe, CheckCircle } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';
import { useSfx, SoundKey } from '../../../store/sfxStore';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "Fact or Fiction?",
        icon: "🕵️",
        content: (
            <div className="space-y-6">
                <div className="bg-amber-50 p-6 rounded-2xl border-2 border-amber-200">
                    <h3 className="font-bold text-xl text-amber-800 mb-3">Don&apos;t Believe Everything!</h3>
                    <p className="text-slate-700 mb-4">
                        The internet is full of amazing information, but also lies, rumors, and mistakes.
                        Being a <strong>Cyber Detective</strong> means checking the facts before you share.
                    </p>
                    <p className="text-slate-700">
                        Not everything online is true! Can you tell which sources are <strong>trustworthy</strong>?
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "The CRAAP Test",
        icon: "📝",
        content: (
            <div className="space-y-6">
                <p className="text-slate-600 mb-4">
                    Librarians created the <strong>CRAAP Test</strong> to help you figure out if a source is reliable.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                        <span className="font-bold text-amber-700 block mb-1">Currency</span>
                        <p className="text-slate-600 text-sm">When was it published? Is it up to date?</p>
                    </div>
                    <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                        <span className="font-bold text-amber-700 block mb-1">Relevance</span>
                        <p className="text-slate-600 text-sm">Does it relate to your topic?</p>
                    </div>
                    <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                        <span className="font-bold text-amber-700 block mb-1">Authority</span>
                        <p className="text-slate-600 text-sm">Who wrote it? Are they an expert?</p>
                    </div>
                    <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                        <span className="font-bold text-amber-700 block mb-1">Accuracy</span>
                        <p className="text-slate-600 text-sm">Is the info supported by evidence?</p>
                    </div>
                    <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                        <span className="font-bold text-amber-700 block mb-1">Purpose</span>
                        <p className="text-slate-600 text-sm">Why was this written? To sell, persuade, or inform?</p>
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "Decoding the URL",
        icon: "🌐",
        content: (
            <div className="space-y-6">
                <p className="text-slate-600 mb-4">The end of the website address (URL) tells you a lot!</p>
                <div className="grid grid-cols-2 gap-4">
                    <div className="bg-green-50 p-4 rounded-xl border border-green-200 text-center">
                        <div className="font-bold text-green-700 text-xl mb-1">.gov</div>
                        <div className="text-xs text-slate-600 font-bold">Government</div>
                        <div className="text-[10px] text-slate-500 mt-1">Very Reliable</div>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-200 text-center">
                        <div className="font-bold text-blue-700 text-xl mb-1">.edu</div>
                        <div className="text-xs text-slate-600 font-bold">Education</div>
                        <div className="text-[10px] text-slate-500 mt-1">Very Reliable</div>
                    </div>
                    <div className="bg-amber-50 p-4 rounded-xl border border-amber-200 text-center">
                        <div className="font-bold text-amber-700 text-xl mb-1">.org</div>
                        <div className="text-xs text-slate-600 font-bold">Non-Profit</div>
                        <div className="text-[10px] text-slate-500 mt-1">Usually Good (Check bias)</div>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-center">
                        <div className="font-bold text-slate-700 text-xl mb-1">.com</div>
                        <div className="text-xs text-slate-600 font-bold">Commercial</div>
                        <div className="text-[10px] text-slate-500 mt-1">Anyone can buy!</div>
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 4,
        title: "Red Flags",
        icon: "🚩",
        content: (
            <div className="space-y-6">
                <div className="bg-red-50 p-6 rounded-2xl border-2 border-red-100">
                    <h3 className="font-bold text-xl text-red-800 mb-4">Watch out for:</h3>
                    <div className="space-y-4">
                        <div className="flex items-start gap-3">
                            <div className="text-2xl">🤥</div>
                            <div>
                                <span className="font-bold text-red-900 block">Misinformation</span>
                                <span className="text-red-800 text-sm">False info shared by mistake.</span>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="text-2xl">😈</div>
                            <div>
                                <span className="font-bold text-red-900 block">Disinformation</span>
                                <span className="text-red-800 text-sm">Lies spread ON PURPOSE to trick you.</span>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="text-2xl">🎣</div>
                            <div>
                                <span className="font-bold text-red-900 block">Clickbait</span>
                                <span className="text-red-800 text-sm">&quot;You won&apos;t BELIEVE what happened!&quot; headlines.</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="bg-indigo-100 p-4 rounded-xl border border-indigo-200 mt-4 text-center">
                    <h4 className="font-bold text-indigo-800 mb-2">🎮 Ready to Fact Check?</h4>
                    <p className="text-indigo-700 text-sm">
                        In the activity, you&apos;ll act as an editor and decide which stories are real!
                    </p>
                </div>
            </div>
        )
    }
];

// ============ GAME LOGIC ============
interface Source {
    id: number;
    title: string;
    url: string;
    preview: string;
    isReliable: boolean;
    reason: string;
    type: 'news' | 'wiki' | 'blog' | 'edu' | 'random';
}

const SOURCES: Source[] = [
    {
        id: 1,
        title: 'NASA - Mars Exploration',
        url: 'nasa.gov/mars',
        preview: 'Scientists at NASA have sent rovers to Mars to study...',
        isReliable: true,
        reason: '.gov websites are from the government - usually trustworthy!',
        type: 'edu',
    },
    {
        id: 2,
        title: 'CoolFacts123 Blog',
        url: 'coolfacts123.blogspot.com',
        preview: 'Mars is actually made of cheese! My cousin told me...',
        isReliable: false,
        reason: 'Random blogs can say anything - no fact checkers!',
        type: 'blog',
    },
    {
        id: 3,
        title: 'Encyclopedia Britannica',
        url: 'britannica.com/science/mars',
        preview: 'Mars is the fourth planet from the Sun and is known...',
        isReliable: true,
        reason: 'Encyclopedias have editors who check facts!',
        type: 'wiki',
    },
    {
        id: 4,
        title: 'RandomDude2023 on TikTok',
        url: 'tiktok.com/@randomdude2023',
        preview: 'OMG you guys Mars has ALIENS I saw a video...',
        isReliable: false,
        reason: 'Anyone can post on social media - verify with other sources!',
        type: 'random',
    },
    {
        id: 5,
        title: 'MIT Science Journal',
        url: 'science.mit.edu/articles/mars',
        preview: 'Research published by MIT scientists confirms...',
        isReliable: true,
        reason: '.edu websites are from schools and universities!',
        type: 'edu',
    },
    {
        id: 6,
        title: 'BreakingNews4U.net',
        url: 'breakingnews4u.net/shocking',
        preview: 'SHOCKING: Government hiding Mars secrets! Click NOW...',
        isReliable: false,
        reason: 'Words like "SHOCKING" and "CLICK NOW" are warning signs!',
        type: 'news',
    },
];

const ResearchGame = ({ onComplete }: { onComplete: () => void }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [answered, setAnswered] = useState<boolean | null>(null);
    const [gameOver, setGameOver] = useState(false);
    const { play } = useSfx();

    const safePlay = (key: SoundKey) => {
        if (play) {
            try {
                play(key);
            } catch (e) {
                console.warn('SFX Error:', e);
            }
        }
    };

    const currentSource = SOURCES[currentIndex];

    const handleAnswer = (userSaysReliable: boolean) => {
        const correct = userSaysReliable === currentSource.isReliable;
        setAnswered(correct);
        if (correct) {
            setScore(s => s + 1);
            safePlay('success');
        } else {
            safePlay('fail');
        }
    };

    const nextSource = () => {
        if (currentIndex < SOURCES.length - 1) {
            setCurrentIndex(i => i + 1);
            setAnswered(null);
        } else {
            setGameOver(true);
            if (score >= 4) {
                safePlay('glory');
                onComplete();
            } else {
                safePlay('error');
            }
        }
    };

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'edu': return '🎓';
            case 'news': return '📰';
            case 'wiki': return '📚';
            case 'blog': return '✍️';
            default: return '❓';
        }
    };

    const restart = () => {
        setCurrentIndex(0);
        setScore(0);
        setAnswered(null);
        setGameOver(false);
        safePlay('click');
    };

    if (gameOver) {
        return (
            <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white rounded-3xl p-8 shadow-xl text-center max-w-2xl mx-auto border-2 border-slate-100"
            >
                <div className="text-6xl mb-4">📚</div>
                <h2 className="text-3xl font-black text-slate-800 mb-2">Editor&apos;s Report</h2>
                <p className="text-slate-600 mb-6 text-xl">
                    You got <strong>{score}</strong> out of <strong>{SOURCES.length}</strong> right.
                </p>
                {score >= 4 ? (
                    <div className="bg-green-100 text-green-800 p-4 rounded-xl font-bold mb-6">
                        Pass! Excellent work Detective.
                    </div>
                ) : (
                    <div className="bg-amber-100 text-amber-800 p-4 rounded-xl font-bold mb-6">
                        Needs Improvement. Try assessing the sources again!
                    </div>
                )}
                <button
                    onClick={() => { restart(); safePlay('click'); }}
                    className="bg-amber-500 text-white px-8 py-3 rounded-xl font-bold hover:bg-amber-600 transition-colors shadow-lg"
                >
                    Try Again
                </button>
            </motion.div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto">
            {/* Score */}
            <div className="flex justify-center gap-8 mb-6">
                <div className="bg-white px-6 py-3 rounded-2xl shadow-sm border border-slate-200">
                    <div className="text-2xl font-black text-amber-600 text-center">{score}/{SOURCES.length}</div>
                    <div className="text-xs text-slate-500 uppercase font-bold">Accuracy</div>
                </div>
                <div className="bg-white px-6 py-3 rounded-2xl shadow-sm border border-slate-200">
                    <div className="text-2xl font-black text-blue-600 text-center">{currentIndex + 1}</div>
                    <div className="text-xs text-slate-500 uppercase font-bold">Source</div>
                </div>
            </div>

            {/* Source Card */}
            <motion.div
                key={currentSource.id}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="bg-white rounded-3xl shadow-xl overflow-hidden mb-6 border-2 border-slate-100"
            >
                <div className="bg-slate-50 px-6 py-4 border-b flex items-center gap-3">
                    <Globe className="text-slate-400" size={24} />
                    <div className="font-mono text-sm text-slate-600 truncate">{currentSource.url}</div>
                    <span className="ml-auto text-2xl" title={currentSource.type}>{getTypeIcon(currentSource.type)}</span>
                </div>

                <div className="p-8">
                    <h3 className="text-2xl font-bold text-slate-800 mb-4">{currentSource.title}</h3>
                    <p className="text-slate-600 bg-slate-50 p-6 rounded-xl italic text-lg leading-relaxed border-l-4 border-slate-300">
                        &quot;{currentSource.preview}&quot;
                    </p>
                </div>
            </motion.div>

            {/* Answer Buttons */}
            {answered === null ? (
                <div className="grid grid-cols-2 gap-6 mb-6">
                    <button
                        onClick={() => { handleAnswer(true); safePlay('click'); }}
                        onMouseEnter={() => safePlay('hover')}
                        className="bg-green-500 text-white py-6 rounded-2xl font-bold text-xl hover:bg-green-600 flex items-center justify-center gap-3 shadow-lg hover:scale-[1.02] transition-all"
                    >
                        <ThumbsUp size={28} /> Trustworthy!
                    </button>
                    <button
                        onClick={() => { handleAnswer(false); safePlay('click'); }}
                        onMouseEnter={() => safePlay('hover')}
                        className="bg-red-500 text-white py-6 rounded-2xl font-bold text-xl hover:bg-red-600 flex items-center justify-center gap-3 shadow-lg hover:scale-[1.02] transition-all"
                    >
                        <ThumbsDown size={28} /> Not Sure...
                    </button>
                </div>
            ) : (
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className={`p-6 rounded-3xl mb-6 shadow-lg ${answered ? 'bg-green-100 border-2 border-green-300' : 'bg-red-100 border-2 border-red-300'}`}
                >
                    <div className="font-bold text-xl mb-3 flex items-center gap-2">
                        {currentSource.isReliable ? (
                            <><CheckCircle className="text-green-600" /> <span className="text-green-800">This is a RELIABLE source!</span></>
                        ) : (
                            <><span className="text-2xl">⚠️</span> <span className="text-red-800">Be CAREFUL with this one!</span></>
                        )}
                    </div>
                    <div className="bg-white/60 rounded-xl p-4 mb-6">
                        <div className="font-bold text-slate-600 mb-1 text-sm uppercase tracking-wider">Analysis</div>
                        <p className="text-slate-800 font-medium text-lg">{currentSource.reason}</p>
                    </div>
                    <button
                        onClick={() => { nextSource(); safePlay('click'); }}
                        onMouseEnter={() => safePlay('hover')}
                        className="w-full bg-amber-500 text-white px-6 py-4 rounded-xl font-bold hover:bg-amber-600 text-lg shadow-md transition-colors"
                    >
                        Next Source →
                    </button>
                </motion.div>
            )}
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const ResearchSkills = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        setCompleted(true);
        completeModule('research-skills', 100);
    };

    return (
        <LessonWrapper
            moduleName="Fact or Fiction?"
            moduleEmoji="🔍"
            moduleColor="amber"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Fact Checker"
        >
            <ResearchGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default ResearchSkills;
